import { redirectOnLogin } from '@modules/authentication/utils';
import { captureException, captureMessage } from '@sentry/react';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import debounce from 'lodash/debounce';
import AuthService from 'services/AuthService';
import { AmplifyAuth, AmplifyHub } from 'v2/core/clients/authAmplify';

const getToken = (): Promise<CognitoUserSession> =>
  AmplifyAuth.currentSession();

const continueAfterSignIn = (
  redirectState: string | { [key: string]: any } | undefined, // Depending on the event, this can be a string or an object with unknown properties
  searchParams: URLSearchParams,
) => {
  getToken()
    .then(async (userToken: CognitoUserSession) => {
      AuthService.setEmail(userToken.getIdToken().payload.email);
      AuthService.setToken(userToken.getIdToken().getJwtToken());

      if (typeof redirectState === 'string') {
        window.location.href = decodeURIComponent(redirectState);
        return;
      }

      redirectOnLogin(searchParams);
    })
    .catch((e) => {
      captureException(e);
      AuthService.setCustomAuthErrorMessage(
        'There was an error logging in. Please try again.',
      );
      window.location.href = '/logout';
    });
};

const debouncedContinueAfterSignIn = debounce(continueAfterSignIn, 300, {
  trailing: true,
});

export const initializeAmplifyAuth = () => {
  const searchParams = new URLSearchParams(window.location.search);

  AmplifyHub.listen('auth', ({ payload: { event, data, message } }) => {
    switch (event) {
      case 'signIn':
      case 'cognitoHostedUI':
        debouncedContinueAfterSignIn(undefined, searchParams);
        break;
      case 'customOAuthState':
        debouncedContinueAfterSignIn(data, searchParams);
        break;
      case 'signIn_failure':
      case 'cognitoHostedUI_failure':
        captureMessage(`Auth event failure: ${event}`, {
          level: 'info',
          extra: {
            message,
            email: AuthService.getEmail(),
            token: AuthService.getToken(),
            data: JSON.stringify(data),
          },
        });
        break;
      default:
        break;
    }
  });
};
